import React, { useEffect, useMemo } from 'react';
import LocationSearch from './LocationSearch';
import MapWrapper from './MapWrapper';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { OrderingSelectors } from 'polygon-ordering';
import { useTranslation } from 'react-i18next';
import { getAddressSuggestionsAsync } from '../libs/polygon-utils/src/GoogleMapsApiHandler';
import _ from 'lodash';
import { SEARCH_DEBOUNCE_DELAY } from '../libs/polygon-ordering/src/constants';
import AddressSuggestions from './AddressSuggestions';
import { updateForm } from '../slices/deliveryAddressForm';
import getUserLocation from '../selectors/getUserLocation';
import SectionContainer from './SectionContainer';
import SubSection from './SubSection';
import getThemeLookup from '../selectors/getThemeLookup';
import { TEXT_PROPERTIES } from '../utils/theme';
import RedcatMapIcon from './icons/redcat/RedcatMapIcon';
import combineStyles from '../utils/combineStyles';
import { filterAddressSuggestions } from '../libs/polygon-utils/src/DeliveryAddress';

const { getMember } = OrderingSelectors;

const AddressSearchInterface = () => {
  const dispatch = useAppDispatch();
  const p = useAppSelector(getThemeLookup);
  const { t } = useTranslation();
  const member = useAppSelector(getMember);

  const formState = useAppSelector(state => state.deliveryAddressForm);

  const googleMapsApiKey = useAppSelector(state => state.config.googleMapsApiKey);
  const googleMapsCountryCode = useAppSelector(state => state.config.geocoderCountryRestriction);

  const userLocation = useAppSelector(getUserLocation);

  const recentAddresses = useAppSelector(state => state.recentLocations);

  const getAddressSuggestions = async (input: string | undefined) => {
    if (!googleMapsApiKey || !googleMapsCountryCode || !input) {
      return [];
    }

    const addresses = await getAddressSuggestionsAsync(
      fetch,
      googleMapsApiKey,
      input,
      'simple',
      googleMapsCountryCode,
      userLocation,
    );

    dispatch(updateForm({ addressSuggestions: addresses }));
  };

  const debouncedSearch = useMemo(
    () => _.debounce(getAddressSuggestions, SEARCH_DEBOUNCE_DELAY),
    [],
  );

  const chooseOnMapClicked = () => {
    dispatch(
      updateForm({
        addressSelectionView: 'Map',
        addressInput: undefined,
        adjustingPin: true,
      }),
    );
  };

  useEffect(() => {
    dispatch(updateForm({ addressSuggestions: [] }));
    debouncedSearch(formState.addressInput);
  }, [formState.addressInput]);

  let addresses: DeliveryAddress[] = filterAddressSuggestions({
    filter: formState.addressInput,
    suggestions: formState.addressSuggestions,
    savedAddresses: member?.savedAddresses ?? [],
    recentAddresses: recentAddresses ?? [],
    editingAddress: !!formState.editingAddress,
  });

  // This is to filter out null values from the list of addresses.
  // This can happen if the address object is malformed or does
  // not match the new DeliveryAddress format.
  addresses = addresses.filter(address => address);

  return (
    <>
      <MapWrapper>
        <LocationSearch
          placeholder={t('placeholder.addDeliveryAddress')}
          containerStyle={styles.locationSearch}
          showClearButton={true}
          autoFocus={true}
        />
      </MapWrapper>
      <SectionContainer style={styles.chooseOnMapContainer}>
        <SubSection
          title={t('deliveryAddress.chooseOnMap')}
          action={chooseOnMapClicked}
          titleStyle={p('chooseOnMapText', TEXT_PROPERTIES)}
          leftIcon={() => <RedcatMapIcon />}
          leftIconStyle={combineStyles(p('chooseOnMapText', TEXT_PROPERTIES), { paddingLeft: 6 })}
        />
      </SectionContainer>
      <AddressSuggestions addresses={addresses} />
    </>
  );
};

const styles: Styles = {
  chooseOnMapContainer: {
    padding: 0,
  },
  locationSearch: {
    marginTop: 12,
    marginBottom: 20,
  },
};

export default React.memo(AddressSearchInterface);
